import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EditDomesticBaseFeeForm } from "./edit-domestic-base-fee-form";
import { EditDimensionalWeightFeeForm } from "./edit-dimensional-weight-fee-form";
import { useState } from "react";

import {
  useGetDimensionalWeightFees,
  useGetDomesticBaseFees,
  useGetSpecialHandlingFees,
  useGetSpeedFees,
} from "@/api/pricings";
import { DomesticBaseFee, DimensionalWeightFee, SpeedFee } from "@/api.types";
import { EditSpeedFeeForm } from "./edit-speed-fee-form";
import { CreateMemberTierButton } from "./create-member-tier-button";
import { CreateFeeButton } from "./create-fee-button";

export function PricingsTable() {
  const { data: domesticBaseFees } = useGetDomesticBaseFees();
  const { data: speedFees } = useGetSpeedFees();
  const { data: specialHandlingFees } = useGetSpecialHandlingFees();
  const { data: dimensionalWeightFees } = useGetDimensionalWeightFees();
  const [selectedDomesticFee, setSelectedDomesticFee] =
    useState<DomesticBaseFee | null>(null);
  const [selectedDimensionalFee, setSelectedDimensionalFee] =
    useState<DimensionalWeightFee | null>(null);
  const [selectedSpeedFee, setSelectedSpeedFee] = useState<SpeedFee | null>(
    null
  );

  return (
    <div>
      <div className="flex justify-end mb-4 gap-4">
        <CreateFeeButton />
        <CreateMemberTierButton />
      </div>
      <div className="space-y-4">
        <Tabs defaultValue="domestic-base">
          <TabsList>
            <TabsTrigger value="domestic-base">Domestic Base Fees</TabsTrigger>
            <TabsTrigger value="dimensional-weight">
              Dimensional Weight
            </TabsTrigger>
            <TabsTrigger value="speed">Speed Fees</TabsTrigger>
            <TabsTrigger value="special-handling">Special Handling</TabsTrigger>
            <TabsTrigger value="weight-surcharge">Weight Surcharge</TabsTrigger>
          </TabsList>

          <TabsContent value="domestic-base">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Pickup Area</TableHead>
                    <TableHead>Delivery Area</TableHead>
                    <TableHead>Base Price</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domesticBaseFees?.data.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell>{fee.name}</TableCell>
                      <TableCell>{fee.pickupServiceArea.name}</TableCell>
                      <TableCell>{fee.deliveryServiceArea.name}</TableCell>
                      <TableCell>${fee.basePrice.toFixed(2)}</TableCell>
                      <TableCell>
                        {fee.customerTier?.name ?? "All Tiers"}
                        {fee.customerTier?.type === "DEFAULT" && " (Default)"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={fee.isActive ? "default" : "outline"}>
                          {fee.isActive ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedDomesticFee(fee)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="dimensional-weight">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Weight Range</TableHead>
                    <TableHead>Price/kg</TableHead>
                    <TableHead>Base Fee</TableHead>
                    <TableHead>Max Side Length</TableHead>
                    <TableHead>Factor</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dimensionalWeightFees?.data.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell>{fee.name}</TableCell>
                      <TableCell>
                        {fee.minWeight}kg - {fee.maxWeight}kg
                      </TableCell>
                      <TableCell>${fee.pricePerKg.toFixed(2)}</TableCell>
                      <TableCell>${fee.fee.toFixed(2)}</TableCell>
                      <TableCell>{fee.maxSideLength}cm</TableCell>
                      <TableCell>{fee.factor}</TableCell>
                      <TableCell>{fee.feeScope}</TableCell>
                      <TableCell>
                        {fee.customerTier?.name ?? "All Tiers"}
                        {fee.customerTier?.type === "DEFAULT" && " (Default)"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={fee.isActive ? "default" : "outline"}>
                          {fee.isActive ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedDimensionalFee(fee)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="speed">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Delivery Time</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Pickup Area</TableHead>
                    <TableHead>Delivery Area</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {speedFees?.data.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell>{fee.name}</TableCell>
                      <TableCell>{fee.type}</TableCell>
                      <TableCell>{fee.description}</TableCell>
                      <TableCell>${fee.fee.toFixed(2)}</TableCell>
                      <TableCell>
                        {fee.minDeliveryTime && fee.maxDeliveryTime ? (
                          <>
                            {fee.minDeliveryTime}-{fee.maxDeliveryTime}{" "}
                            {fee.timeUnit ? fee.timeUnit.toLowerCase() : ""}
                          </>
                        ) : fee.minDeliveryTime ? (
                          <>
                            {fee.minDeliveryTime}+{" "}
                            {fee.timeUnit ? fee.timeUnit.toLowerCase() : ""}
                          </>
                        ) : fee.maxDeliveryTime ? (
                          <>
                            Up to {fee.maxDeliveryTime}{" "}
                            {fee.timeUnit ? fee.timeUnit.toLowerCase() : ""}
                          </>
                        ) : (
                          "Not specified"
                        )}
                      </TableCell>
                      <TableCell>{fee.feeScope}</TableCell>
                      <TableCell>
                        {fee.pickupServiceArea?.name ?? "Any"}
                      </TableCell>
                      <TableCell>
                        {fee.deliveryServiceArea?.name ?? "Any"}
                      </TableCell>
                      <TableCell>
                        {fee.customerTier?.name ?? "All Tiers"}
                        {fee.customerTier?.type === "DEFAULT" && " (Default)"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={fee.isActive ? "default" : "outline"}>
                          {fee.isActive ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedSpeedFee(fee)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="special-handling">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {specialHandlingFees?.data.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell>{fee.name}</TableCell>
                      <TableCell>{fee.type}</TableCell>
                      <TableCell>${fee.fee.toFixed(2)}</TableCell>
                      <TableCell>{fee.feeScope}</TableCell>
                      <TableCell>
                        {fee.customerTier?.name ?? "All Tiers"}
                        {fee.customerTier?.type === "DEFAULT" && " (Default)"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={fee.isActive ? "default" : "outline"}>
                          {fee.isActive ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {selectedDomesticFee && (
          <EditDomesticBaseFeeForm
            fee={selectedDomesticFee}
            onClose={() => setSelectedDomesticFee(null)}
          />
        )}

        {selectedDimensionalFee && (
          <EditDimensionalWeightFeeForm
            fee={selectedDimensionalFee}
            onClose={() => setSelectedDimensionalFee(null)}
          />
        )}

        {selectedSpeedFee && (
          <EditSpeedFeeForm
            fee={selectedSpeedFee}
            onClose={() => setSelectedSpeedFee(null)}
          />
        )}
      </div>
    </div>
  );
}
